import { Fragment, cloneElement, isValidElement, type ReactNode } from 'react';
import { renderHighlightedText } from './searchHighlightUtils';

const SEARCH_HIGHLIGHT_ELLIPSIS = '...';

type SliceNodeTextResult = {
  node: ReactNode;
  nextIndex: number;
  hasContent: boolean;
};

type FocusedSearchSlice = {
  start: number;
  end: number;
  hasPrefixEllipsis: boolean;
  hasSuffixEllipsis: boolean;
};

const sliceNodeText = (node: ReactNode, start: number, end: number, currentIndex = 0): SliceNodeTextResult => {
  if (typeof node === 'string' || typeof node === 'number') {
    const text = String(node);
    const nextIndex = currentIndex + text.length;
    const sliceStart = Math.max(0, start - currentIndex);
    const sliceEnd = Math.min(text.length, end - currentIndex);

    if (sliceStart >= sliceEnd) {
      return {
        node: null,
        nextIndex,
        hasContent: false,
      };
    }

    return {
      node: text.slice(sliceStart, sliceEnd),
      nextIndex,
      hasContent: true,
    };
  }

  if (Array.isArray(node)) {
    const slicedNodes: ReactNode[] = [];
    let nextIndex = currentIndex;

    node.forEach(childNode => {
      const childResult = sliceNodeText(childNode, start, end, nextIndex);
      nextIndex = childResult.nextIndex;

      if (childResult.hasContent) {
        slicedNodes.push(childResult.node);
      }
    });

    return {
      node: slicedNodes,
      nextIndex,
      hasContent: slicedNodes.length > 0,
    };
  }

  if (isValidElement<{ children?: ReactNode }>(node)) {
    if (node.props.children === undefined) {
      return {
        node: null,
        nextIndex: currentIndex,
        hasContent: false,
      };
    }

    const childResult = sliceNodeText(node.props.children, start, end, currentIndex);

    if (!childResult.hasContent) {
      return {
        node: null,
        nextIndex: childResult.nextIndex,
        hasContent: false,
      };
    }

    return {
      node: cloneElement(node, undefined, childResult.node),
      nextIndex: childResult.nextIndex,
      hasContent: true,
    };
  }

  return {
    node: null,
    nextIndex: currentIndex,
    hasContent: false,
  };
};

export const highlightNodeText = (node: ReactNode, searchTerms: string[], highlightClassName: string): ReactNode => {
  if (typeof node === 'string') {
    return renderHighlightedText(node, searchTerms, highlightClassName);
  }

  if (typeof node === 'number') {
    return renderHighlightedText(String(node), searchTerms, highlightClassName);
  }

  if (Array.isArray(node)) {
    return node.map((childNode, childIndex) => (
      <Fragment key={`${childIndex}`}>{highlightNodeText(childNode, searchTerms, highlightClassName)}</Fragment>
    ));
  }

  if (isValidElement<{ children?: ReactNode }>(node)) {
    if (node.props.children === undefined) {
      return node;
    }

    return cloneElement(node, undefined, highlightNodeText(node.props.children, searchTerms, highlightClassName));
  }

  return node;
};

export const renderFocusedHighlightedNode = (
  node: ReactNode,
  focusedSearchSlice: FocusedSearchSlice | null,
  searchTerms: string[],
  highlightClassName: string,
): ReactNode => {
  if (!focusedSearchSlice) {
    return highlightNodeText(node, searchTerms, highlightClassName);
  }

  const slicedNodeResult = sliceNodeText(node, focusedSearchSlice.start, focusedSearchSlice.end);

  if (!slicedNodeResult.hasContent) {
    return highlightNodeText(node, searchTerms, highlightClassName);
  }

  const focusedNode = (
    <>
      {focusedSearchSlice.hasPrefixEllipsis ? SEARCH_HIGHLIGHT_ELLIPSIS : null}
      {slicedNodeResult.node}
      {focusedSearchSlice.hasSuffixEllipsis ? SEARCH_HIGHLIGHT_ELLIPSIS : null}
    </>
  );

  return highlightNodeText(focusedNode, searchTerms, highlightClassName);
};